import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import axios from 'axios';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useQueries, useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import processingActivitiesApi, { loadProcessingActivityForEditor } from 'config/api/processingActivities/processingActivities';
import { extractContextualValidationMessages } from 'config/api/processingActivities/processingActivities.parsers';
import type {
  NeverPublishedProcessingActivity,
  ProcessingActivityCreateDTO,
  RopaCategory,
  RopaCategoryTypeEnum,
  SharedDraftOutput,
} from 'config/api/processingActivities/processingActivities.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useUnsavedChangesProtection from 'hooks/useUnsavedChangesProtection/useUnsavedChangesProtection';
import processing_activities_messages from 'messages/processing_activities_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import PATHS from 'router/PATHS';

import {
  parseProcessingActivityDetailToEditorForm,
  parseProcessingActivityEditorFormForDraftBE,
  parseProcessingActivityEditorFormForNeverPublishedSave,
  parseSharedDraftOutputToEditorForm,
} from './processingActivityEditor.parsers';
import { EMPTY_PROCESSING_ACTIVITY_EDITOR_FORM } from './processingActivityEditor.types';
import {
  buildDraftValidationSchema,
  buildProcessingActivityEditorValidationMessages,
  buildPublishDisabledTooltip,
  buildPurposeRetentionPublishMessage,
  hasInvalidPurposeRetention,
  hasValidationErrors,
  mapProcessingActivityPurposeRetentionApiErrors,
  validateProcessingActivityEditorForPublish,
} from './processingActivityEditor.validation';

type UseProcessingActivityEditorOptions = {
  isCreateMode: boolean;
  organizationId?: number;
  processingActivityId?: number;
};

type SaveDraftResult = {
  activityId: number;
  draftRevision?: number;
  isNeverPublished: boolean;
};

const ROPA_CATEGORY_TYPES: RopaCategoryTypeEnum[] = ['data_subject', 'personal_data', 'recipient'];

const useProcessingActivityEditor = ({ isCreateMode, organizationId, processingActivityId }: UseProcessingActivityEditorOptions) => {
  const { t } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { showConfirmationModal } = useConfirmationModalContext();
  const [activityId, setActivityId] = useState<number | undefined>(processingActivityId);
  const [draftRevision, setDraftRevision] = useState<number | undefined>();
  const [basePublishedVersionNumber, setBasePublishedVersionNumber] = useState<number | null>(null);
  const [isNeverPublished, setIsNeverPublished] = useState(isCreateMode);
  const [isSaving, setIsSaving] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [backendPurposeErrors, setBackendPurposeErrors] = useState<ReturnType<typeof validateProcessingActivityEditorForPublish>>({});
  const saveHandlerRef = useRef<(() => Promise<void>) | null>(null);
  const isEditorInitializedRef = useRef(false);

  const effectiveActivityId = activityId ?? processingActivityId;

  const invalidateRopaCategories = useCallback(async () => {
    if (!organizationId) return;
    await queryClient.invalidateQueries([QUERY_KEYS.PROCESSING_ACTIVITIES_ROPA_CATEGORIES, organizationId]);
  }, [organizationId, queryClient]);

  const editorQuery = useQuery(
    [QUERY_KEYS.PROCESSING_ACTIVITIES_GET_DRAFT, organizationId, effectiveActivityId],
    () => loadProcessingActivityForEditor(organizationId as number, effectiveActivityId as number),
    {
      enabled: Boolean(organizationId && effectiveActivityId && !isCreateMode),
    },
  );

  const categoriesQueries = useQueries(
    ROPA_CATEGORY_TYPES.map(categoryType => ({
      queryKey: [QUERY_KEYS.PROCESSING_ACTIVITIES_ROPA_CATEGORIES, organizationId, categoryType],
      queryFn: () => processingActivitiesApi.getRopaCategories(organizationId as number, { category_type: categoryType }),
      enabled: Boolean(organizationId),
    })),
  );

  const ropaCategoriesByType = useMemo(
    () =>
      ROPA_CATEGORY_TYPES.reduce(
        (accumulator, categoryType, index) => {
          accumulator[categoryType] = categoriesQueries[index]?.data?.data.results ?? [];
          return accumulator;
        },
        {
          data_subject: [] as RopaCategory[],
          personal_data: [] as RopaCategory[],
          recipient: [] as RopaCategory[],
        },
      ),
    [categoriesQueries],
  );

  const formik = useFormik({
    initialValues: EMPTY_PROCESSING_ACTIVITY_EDITOR_FORM,
    onSubmit: () => undefined,
    validationSchema: buildDraftValidationSchema(),
  });

  const validationMessages = useMemo(() => buildProcessingActivityEditorValidationMessages(t), [t]);

  const publishValidationErrors = useMemo(
    () => ({
      ...validateProcessingActivityEditorForPublish(formik.values, validationMessages),
      ...backendPurposeErrors,
    }),
    [backendPurposeErrors, formik.values, validationMessages],
  );

  const canPublish = !hasValidationErrors(publishValidationErrors);
  const publishDisabledTooltip = useMemo(() => buildPublishDisabledTooltip(publishValidationErrors), [publishValidationErrors]);
  const showPublishValidationErrors = (formik.dirty && !canPublish) || hasInvalidPurposeRetention(publishValidationErrors);
  const visiblePublishValidationErrors = showPublishValidationErrors ? publishValidationErrors : {};

  useEffect(() => {
    setBackendPurposeErrors({});
  }, [formik.values.purposes]);

  useEffect(() => {
    isEditorInitializedRef.current = false;
  }, [processingActivityId]);

  useEffect(() => {
    if (!editorQuery.data?.data || isEditorInitializedRef.current) return;

    const result = editorQuery.data.data;
    isEditorInitializedRef.current = true;

    if (result.mode === 'never-published') {
      setActivityId(result.data.id);
      setDraftRevision(undefined);
      setBasePublishedVersionNumber(null);
      setIsNeverPublished(true);
      formik.resetForm({ values: parseProcessingActivityDetailToEditorForm(result.data) });
      return;
    }

    const draft = result.data;
    setActivityId(draft.processingActivityId);
    setDraftRevision(draft.draftRevision);
    setBasePublishedVersionNumber(draft.basePublishedVersionNumber);
    setIsNeverPublished(false);
    formik.resetForm({ values: parseSharedDraftOutputToEditorForm(draft) });
  }, [editorQuery.data]);

  const applyDraftResponse = useCallback(
    (draft: SharedDraftOutput) => {
      setActivityId(draft.processingActivityId);
      setDraftRevision(draft.draftRevision);
      setBasePublishedVersionNumber(draft.basePublishedVersionNumber);
      setIsNeverPublished(false);
      formik.resetForm({ values: parseSharedDraftOutputToEditorForm(draft) });
    },
    [formik],
  );

  const applyNeverPublishedResponse = useCallback(
    (activity: NeverPublishedProcessingActivity) => {
      setActivityId(activity.id);
      setDraftRevision(undefined);
      setBasePublishedVersionNumber(null);
      setIsNeverPublished(true);
      formik.resetForm({ values: parseProcessingActivityDetailToEditorForm(activity) });
    },
    [formik],
  );

  const ensureActivityExists = useCallback(async () => {
    if (!organizationId) {
      throw new Error('Organization is required.');
    }

    let currentActivityId = activityId;

    if (!currentActivityId) {
      const trimmedName = formik.values.name.trim();
      if (!trimmedName) {
        formik.setFieldTouched('name', true);
        throw new Error('Name is required.');
      }

      const createPayload: ProcessingActivityCreateDTO = { name: trimmedName };
      if (formik.values.processingRole) {
        createPayload.processing_role = formik.values.processingRole;
      }

      const { data: created } = await processingActivitiesApi.create(organizationId, createPayload);
      currentActivityId = created.id;
      setActivityId(currentActivityId);
      setIsNeverPublished(true);
      setBasePublishedVersionNumber(null);
      setDraftRevision(undefined);
    }

    return currentActivityId;
  }, [activityId, formik.setFieldTouched, formik.values.name, formik.values.processingRole, organizationId]);

  const ensureSharedDraft = useCallback(
    async (currentActivityId: number) => {
      if (!organizationId) {
        throw new Error('Organization is required.');
      }

      try {
        const { data: draft } = await processingActivitiesApi.getDraft(organizationId, currentActivityId);
        if (draft.draftRevision != null) {
          setDraftRevision(draft.draftRevision);
        }
        return draft.draftRevision;
      } catch (error) {
        if (!axios.isAxiosError(error) || error.response?.status !== 404) {
          throw error;
        }

        const { data: draft } = await processingActivitiesApi.openDraft(organizationId, currentActivityId);
        setDraftRevision(draft.draftRevision);
        return draft.draftRevision;
      }
    },
    [organizationId],
  );

  const handleNavigateBack = useCallback(() => {
    history.push(PATHS.PROCESSING_ACTIVITIES);
  }, [history]);

  const { allowNextNavigation, handleNavigation, NavigationPrompt } = useUnsavedChangesProtection({
    isDirty: formik.dirty,
    onSave: async () => {
      if (saveHandlerRef.current) {
        await saveHandlerRef.current();
      }
    },
    onNavigate: handleNavigateBack,
    enabled: true,
  });

  const handleDraftConflict = useCallback(async () => {
    enqueueSnackbar(t(processing_activities_messages.editor.errors.draft_conflict), { variant: 'error' });
    await queryClient.invalidateQueries([QUERY_KEYS.PROCESSING_ACTIVITIES_GET_DRAFT, organizationId, effectiveActivityId]);
  }, [effectiveActivityId, enqueueSnackbar, organizationId, queryClient, t]);

  const applyPurposeRetentionApiErrors = useCallback(
    (error: unknown, fallbackMessage: string): boolean => {
      if (!axios.isAxiosError(error) || error.response?.status !== 400) {
        return false;
      }

      const mapped = mapProcessingActivityPurposeRetentionApiErrors(error.response?.data, formik.values.purposes, {
        purposeRetentionInvalid: validationMessages.purposeRetentionInvalid,
        purposeRetentionPublish: index => buildPurposeRetentionPublishMessage(t, index),
      });

      if (!hasInvalidPurposeRetention(mapped)) {
        return false;
      }

      setBackendPurposeErrors(mapped);
      enqueueSnackbar(mapped.purposes || fallbackMessage, { variant: 'error' });
      return true;
    },
    [enqueueSnackbar, formik.values.purposes, t, validationMessages.purposeRetentionInvalid],
  );

  const saveDraft = useCallback(
    async (options?: { changeComment?: string }): Promise<SaveDraftResult> => {
      if (!organizationId) {
        throw new Error('Organization is required.');
      }

      try {
        await buildDraftValidationSchema().validate(formik.values, { abortEarly: false });
      } catch {
        enqueueSnackbar(t(processing_activities_messages.editor.errors.validation_failed), { variant: 'error' });
        throw new Error('Validation failed');
      }

      if (hasInvalidPurposeRetention(validateProcessingActivityEditorForPublish(formik.values, validationMessages))) {
        enqueueSnackbar(validationMessages.purposeRetentionInvalid, { variant: 'error' });
        throw new Error('Validation failed');
      }

      setIsSaving(true);

      try {
        const shouldReplaceUrl = !processingActivityId;
        const currentActivityId = await ensureActivityExists();

        if (isNeverPublished) {
          const payload = parseProcessingActivityEditorFormForNeverPublishedSave(formik.values);
          const { data: activity } = await processingActivitiesApi.updateNeverPublished(organizationId, currentActivityId, payload);
          applyNeverPublishedResponse(activity);
          isEditorInitializedRef.current = true;

          if (shouldReplaceUrl) {
            allowNextNavigation();
            history.replace(PATHS.PROCESSING_ACTIVITIES_EDIT.replace(':id', String(currentActivityId)));
          }

          await queryClient.invalidateQueries([QUERY_KEYS.PROCESSING_ACTIVITIES_GET_ALL, organizationId]);
          await invalidateRopaCategories();
          enqueueSnackbar(t(processing_activities_messages.editor.success.saved), { variant: 'success' });

          return {
            activityId: activity.id,
            isNeverPublished: true,
          };
        }

        const currentRevision = await ensureSharedDraft(currentActivityId);
        const payload = parseProcessingActivityEditorFormForDraftBE(formik.values, {
          draftRevision: currentRevision ?? draftRevision,
          changeComment: options?.changeComment,
        });

        const { data: draft } = await processingActivitiesApi.updateDraft(organizationId, currentActivityId, payload);
        applyDraftResponse(draft);
        isEditorInitializedRef.current = true;

        if (shouldReplaceUrl) {
          allowNextNavigation();
          history.replace(PATHS.PROCESSING_ACTIVITIES_EDIT.replace(':id', String(currentActivityId)));
        }

        await queryClient.invalidateQueries([QUERY_KEYS.PROCESSING_ACTIVITIES_GET_ALL, organizationId]);
        await invalidateRopaCategories();
        enqueueSnackbar(t(processing_activities_messages.editor.success.saved), { variant: 'success' });

        return {
          activityId: draft.processingActivityId,
          draftRevision: draft.draftRevision,
          isNeverPublished: false,
        };
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          await handleDraftConflict();
        } else if (!applyPurposeRetentionApiErrors(error, t(processing_activities_messages.editor.errors.save_failed))) {
          const backendMessages = axios.isAxiosError(error) ? extractContextualValidationMessages(error.response?.data) : [];
          enqueueSnackbar(backendMessages.join(' ') || t(processing_activities_messages.editor.errors.save_failed), {
            variant: 'error',
          });
        }
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [
      allowNextNavigation,
      applyDraftResponse,
      applyNeverPublishedResponse,
      applyPurposeRetentionApiErrors,
      draftRevision,
      ensureActivityExists,
      ensureSharedDraft,
      enqueueSnackbar,
      formik,
      handleDraftConflict,
      history,
      invalidateRopaCategories,
      isNeverPublished,
      organizationId,
      processingActivityId,
      queryClient,
      t,
      validationMessages,
    ],
  );

  saveHandlerRef.current = async () => {
    await saveDraft();
  };

  const publishDraft = useCallback(
    async (changeComment: string) => {
      if (!organizationId) return;

      if (!canPublish) {
        enqueueSnackbar(t(processing_activities_messages.editor.validation.publish_blocked), { variant: 'warning' });
        return;
      }

      setIsSaving(true);

      try {
        let saved: SaveDraftResult;
        try {
          saved = await saveDraft({ changeComment });
        } catch {
          return;
        }

        const currentActivityId = saved.activityId;

        if (saved.isNeverPublished) {
          await processingActivitiesApi.publish(organizationId, currentActivityId, {});
        } else {
          if (saved.draftRevision == null) {
            throw new Error('Draft revision is missing.');
          }

          await processingActivitiesApi.publish(organizationId, currentActivityId, {
            draftRevision: saved.draftRevision,
            changeComment,
          });
        }

        await queryClient.invalidateQueries([QUERY_KEYS.PROCESSING_ACTIVITIES_GET_ALL, organizationId]);
        await queryClient.invalidateQueries([QUERY_KEYS.PROCESSING_ACTIVITIES_METRICS, organizationId]);
        await invalidateRopaCategories();
        enqueueSnackbar(t(processing_activities_messages.editor.success.published), { variant: 'success' });
        allowNextNavigation();
        history.push(PATHS.PROCESSING_ACTIVITIES);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          await handleDraftConflict();
        } else if (!applyPurposeRetentionApiErrors(error, t(processing_activities_messages.editor.errors.publish_failed))) {
          const backendMessages = axios.isAxiosError(error) ? extractContextualValidationMessages(error.response?.data) : [];
          enqueueSnackbar(backendMessages.join(' ') || t(processing_activities_messages.editor.errors.publish_failed), {
            variant: 'error',
          });
        }
      } finally {
        setIsSaving(false);
        setPublishDialogOpen(false);
      }
    },
    [
      allowNextNavigation,
      applyPurposeRetentionApiErrors,
      canPublish,
      enqueueSnackbar,
      handleDraftConflict,
      history,
      invalidateRopaCategories,
      organizationId,
      queryClient,
      saveDraft,
      t,
    ],
  );

  const handlePublishClick = () => {
    if (!canPublish) return;

    setPublishDialogOpen(true);
  };

  const canDiscardDraft = Boolean(!isCreateMode && !isNeverPublished && effectiveActivityId);

  const discardDraft = useCallback(async () => {
    if (!organizationId || !effectiveActivityId || !canDiscardDraft) return;

    const confirmed = await showConfirmationModal({
      title: t(processing_activities_messages.editor.discard_draft.title),
      body: t(processing_activities_messages.editor.discard_draft.body),
      yesLabel: t(processing_activities_messages.editor.actions.discard_draft),
    });

    if (!confirmed) return;

    setIsSaving(true);

    try {
      await processingActivitiesApi.deleteDraft(organizationId, effectiveActivityId);
      await queryClient.invalidateQueries([QUERY_KEYS.PROCESSING_ACTIVITIES_GET_ALL, organizationId]);
      await queryClient.invalidateQueries([QUERY_KEYS.PROCESSING_ACTIVITIES_GET_DRAFT, organizationId, effectiveActivityId]);
      enqueueSnackbar(t(processing_activities_messages.editor.discard_draft.success), { variant: 'success' });
      allowNextNavigation();
      history.push(PATHS.PROCESSING_ACTIVITIES);
    } catch {
      enqueueSnackbar(t(processing_activities_messages.editor.discard_draft.error), { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [
    allowNextNavigation,
    canDiscardDraft,
    effectiveActivityId,
    enqueueSnackbar,
    history,
    organizationId,
    queryClient,
    showConfirmationModal,
    t,
  ]);

  return {
    activityId,
    allowNextNavigation,
    basePublishedVersionNumber,
    canDiscardDraft,
    canPublish,
    discardDraft,
    publishDisabledTooltip,
    publishValidationErrors: visiblePublishValidationErrors,
    ropaCategoriesByType,
    draftQuery: editorQuery,
    formik,
    handleNavigation,
    handlePublishClick,
    isCreateMode,
    isSaving,
    NavigationPrompt,
    publishDialogOpen,
    publishDraft,
    saveDraft,
    setPublishDialogOpen,
  };
};

export default useProcessingActivityEditor;
